import { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';
import { ApolloLink } from '@apollo/client';
import { getOperationName } from '@apollo/client/utilities';
import { ApolloErrorCode } from '@src/enums/apolloErrorCode.enum';
import { CompanyByProfileDocument, CompanyDocument, GetVerifiedUserForChatPageDocument } from '@src/graphql/generated';
import { captureError } from '@src/util/error';
import { getIsAuthError } from '../utils/getIsAuthError';
import { refreshTokenAndRetry } from '../utils/refreshTokenAndRetry';

const OPERATIONS_TO_SKIP = [GetVerifiedUserForChatPageDocument, CompanyDocument, CompanyByProfileDocument].map(
  operation => getOperationName(operation),
);

export const errorLink: ApolloLink = onError(({ graphQLErrors, operation, forward }) => {
  if (graphQLErrors) {
    const isAuthError = getIsAuthError(graphQLErrors);

    if (isAuthError) {
      graphQLErrors?.forEach(error => {
        if (['jwt expired', 'jwt must be provided'].includes(error.message)) {
          captureError(error);
        }
      });

      return refreshTokenAndRetry(operation, forward);
    } else if (graphQLErrors.length) {
      if (
        OPERATIONS_TO_SKIP.includes(operation.operationName) &&
        graphQLErrors[0]?.extensions?.code === ApolloErrorCode.RESOURCE_NOT_FOUND
      ) {
        return;
      }

      Sentry.withScope(scope => {
        scope.setTransactionName(operation.operationName);
        scope.setContext('apolloGraphQLOperation', {
          extensions: operation.extensions,
          operationName: operation.operationName,
          variables: operation.variables,
        });

        graphQLErrors?.forEach(error => {
          Sentry.captureMessage(
            `[${operation.operationName}]: ${error.message}` || `[${operation.operationName}]: Missing error message`,
            {
              contexts: {
                apolloGraphQLError: {
                  error: error,
                },
              },
              level: 'error',
            },
          );
        });
      });
    }
  }
});
